import { DATASETS } from '@/components/Constants';

type EntityView = 'GENES' | 'DISEASES' | 'VARIANTS';

type DetailValueLink =
  | {
      type: 'external';
      href: string;
    }
  | {
      type: 'entity';
      entityView: EntityView;
      item: Record<string, unknown>;
    };

function toText(value: unknown): string {
  if (value == null) return '';
  return String(value).trim();
}

function isSingleToken(value: string): boolean {
  return value.length > 0 && !value.includes(',') && !value.includes(';') && !value.includes(' ');
}

function getPubmedLink(value: string): DetailValueLink | null {
  const compact = value.replace(/\s+/g, '');
  if (!compact) return null;
  if (/^\d+$/.test(compact)) {
    return { type: 'external', href: `https://pubmed.ncbi.nlm.nih.gov/${compact}/` };
  }
  return null;
}

export function getDetailValueLink(key: string, value: unknown): DetailValueLink | null {
  const normalizedKey = key.toLowerCase().replace(/[_\s-]/g, '');
  const text = toText(value);
  if (!text || text === '-') return null;

  if (normalizedKey === 'pmid' || normalizedKey === 'firstref' || normalizedKey === 'lastref') {
    return getPubmedLink(text);
  }

  if (normalizedKey.includes('nctid') && isSingleToken(text)) {
    return { type: 'external', href: `https://clinicaltrials.gov/search?id=${encodeURIComponent(text)}` };
  }

  if ((normalizedKey.includes('geneid') || normalizedKey === 'genencbiid') && /^\d+$/.test(text)) {
    return {
      type: 'entity',
      entityView: DATASETS.GENES as EntityView,
      item: { id: text, geneID: text },
    };
  }

  if (
    normalizedKey === 'diseaseumlscui' ||
    normalizedKey === 'diseaseid' ||
    (normalizedKey.includes('disease') && normalizedKey.endsWith('cui'))
  ) {
    return {
      type: 'entity',
      entityView: DATASETS.DISEASES as EntityView,
      item: { id: text, diseaseID: text },
    };
  }

  if (
    normalizedKey === 'variantstrid' ||
    normalizedKey === 'strid' ||
    normalizedKey === 'dbsnp' ||
    normalizedKey === 'dbsnpid'
  ) {
    return {
      type: 'entity',
      entityView: DATASETS.VARIANTS as EntityView,
      item: { id: text, strID: text },
    };
  }

  return null;
}

export function openEntityInfoPanel(entityView: EntityView, item: Record<string, unknown>) {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(
    new CustomEvent('disgenet:open-left-info-panel', {
      detail: { item: { ...item, entityView } },
    }),
  );
}
